import CoreBase from '../../../services/engines/CoreBase';
import { WindowDisplayStates } from './WindowDisplayStates';

const _name = 'WindowGeoObj';

/**
 * WindowGeoObj — data container for a JxForm window's persisted geometry + state.
 * Mirrors CalEventObj pattern: straight field assignment in constructor,
 * derived values (pos) computed via a single private function, mutations
 * always go through a setter so posX/posY/pos (and restoreGeo) can never drift.
 *
 * displayState and isPinned are orthogonal — see WindowDisplayStates comment.
 * All mutators are wrapped in try/catch + logError so breakpoints/log entries
 * can be set per method (this._name.method: message) instead of only at call site.
 */
export default class WindowGeoObj extends CoreBase {
    /**
     * @param {object} data
     * @param {string} data.rk - windowId (alias rk, same convention as JxWindowCache)
     * @param {number} data.posX
     * @param {number} data.posY
     * @param {number} [data.width]
     * @param {number} [data.height]
     * @param {boolean} [data.isPinned]
     * @param {string} [data.displayState] - WindowDisplayStates value
     * @param {object|null} [data.restoreGeo] - { posX, posY, width, height } saved before Minimize/Maximize
     * @param {object|null} [data.targetAnchor] - { x, y } dock/taskbar slot for Minimized state
     * @param {string} [data.tabId] - required by windowPositionPublisher/BE, dropping this causes BAD_REQUEST
     * @param {number} [data.ems] - cache last-modified timestamp, set by JxWindowCache.setByWindowId
     */
    constructor(data) {
        super();
        this._name = _name;

        try {
            this.rk = data.rk;
            this.posX = data.posX ?? 0;
            this.posY = data.posY ?? 0;
            this.width = data.width ?? 0;
            this.height = data.height ?? 0;

            // passthrough — not owned by this class, but must survive the
            // plain-object → WindowGeoObj → plain-object round trip in
            // JxWindowCache.setByWindowId, since windowPositionPublisher
            // posts the whole object straight to the BE.
            this.tabId = data.tabId;
            this.ems = data.ems;

            this.isPinned = data.isPinned ?? false;
            this.displayState = data.displayState ?? WindowDisplayStates.Normal;

            // only set while displayState !== Normal — null otherwise
            this.restoreGeo = data.restoreGeo ?? null;
            // only set while displayState === Minimized — null otherwise
            this.targetAnchor = data.targetAnchor ?? null;

            this.pos = _derivePos(this.posX, this.posY);
        } catch (error) {
            this.logError('constructor', error.message);
        }
    }

    /**
     * True if this window has a real, persisted position (posX/posY > 0),
     * as opposed to never having been placed.
     * @returns {boolean}
     */
    hasRealPos() {
        try {
            return this.posX > 0 && this.posY > 0;
        } catch (error) {
            this.logError('hasRealPos', error.message);
            return false;
        }
    }

    /**
     * Persists a new position — posX/posY and pos in 1 step.
     * @param {{ top: number, left: number }} pos
     */
    setPos(pos) {
        try {
            this.posX = pos.left;
            this.posY = pos.top;
            this.pos = _derivePos(this.posX, this.posY);
        } catch (error) {
            this.logError('setPos', error.message);
        }
    }

    /**
     * @param {{ width: number, height: number }} size
     */
    setSize(size) {
        try {
            this.width = size.width;
            this.height = size.height;
        } catch (error) {
            this.logError('setSize', error.message);
        }
    }

    togglePin() {
        try {
            this.isPinned = !this.isPinned;
        } catch (error) {
            this.logError('togglePin', error.message);
        }
    }

    /**
     * Saves current pos/size to restoreGeo, moves to Minimized, sets targetAnchor.
     * No-op if already Minimized.
     * @param {{ x: number, y: number }} targetAnchor - dock/taskbar slot to animate to
     */
    minimize(targetAnchor) {
        try {
            if (this.displayState === WindowDisplayStates.Minimized) return;

            this.restoreGeo = { posX: this.posX, posY: this.posY, width: this.width, height: this.height };
            this.targetAnchor = targetAnchor;
            this.displayState = WindowDisplayStates.Minimized;
        } catch (error) {
            this.logError('minimize', error.message);
        }
    }

    /**
     * Saves current pos/size to restoreGeo, moves to Maximized.
     * No-op if already Maximized. Caller (component) computes actual
     * fullscreen posX/posY/width/height and calls setPos()/setSize() after.
     */
    maximize() {
        try {
            if (this.displayState === WindowDisplayStates.Maximized) return;

            this.restoreGeo = { posX: this.posX, posY: this.posY, width: this.width, height: this.height };
            this.displayState = WindowDisplayStates.Maximized;
        } catch (error) {
            this.logError('maximize', error.message);
        }
    }

    /**
     * Body-only hide — pos/size stay untouched, no restoreGeo needed.
     * No-op if already Collapsed.
     */
    collapse() {
        try {
            if (this.displayState === WindowDisplayStates.Collapsed) return;
            this.displayState = WindowDisplayStates.Collapsed;
        } catch (error) {
            this.logError('collapse', error.message);
        }
    }

    /**
     * Restores pos/size from restoreGeo (Minimized/Maximized) or just
     * flips back to Normal (Collapsed — geo never changed).
     * No-op if already Normal.
     */
    restore() {
        try {
            if (this.displayState === WindowDisplayStates.Normal) return;

            if (this.restoreGeo) {
                this.posX = this.restoreGeo.posX;
                this.posY = this.restoreGeo.posY;
                this.width = this.restoreGeo.width;
                this.height = this.restoreGeo.height;
                this.pos = _derivePos(this.posX, this.posY);
            }

            this.restoreGeo = null;
            this.targetAnchor = null;
            this.displayState = WindowDisplayStates.Normal;
        } catch (error) {
            this.logError('restore', error.message);
        }
    }
}

// ─── helpers ──────────────────────────────────────────

/**
 * Derives { top, left } from posX/posY — 1-op-1 met CalEventObj._derivePos(),
 * geen fallback-positie hier: caller (JxFormDraggableBase.getInitialPos)
 * blijft verantwoordelijk voor het default-geval (0/0 → nog nooit geplaatst).
 * @param {number} posX
 * @param {number} posY
 * @returns {{ top: number, left: number }}
 */
function _derivePos(posX, posY) {
    return { top: posY, left: posX };
}
